import { call, put, takeEvery, select } from 'redux-saga/effects';
import { gcApi } from 'config/axios';
import {
    FETCH_GROUPS,
    FETCH_GROUPS_START,
    FETCH_GROUPS_SUCCESS,
    FETCH_GROUPS_FAIL,
    FETCH_ADMINISTERED_GROUPS,
    FETCH_ADMINISTERED_GROUPS_START,
    FETCH_ADMINISTERED_GROUPS_SUCCESS,
    FETCH_ADMINISTERED_GROUPS_FAIL,
    FETCH_GROUP_REQUESTS,
    FETCH_GROUP_REQUESTS_START,
    FETCH_GROUP_REQUESTS_SUCCESS,
    FETCH_GROUP_REQUESTS_FAIL,
    FETCH_UNHANDLED_GROUP_REQUESTS,
    FETCH_UNHANDLED_GROUP_REQUESTS_SUCCESS,
    FETCH_UNHANDLED_GROUP_REQUESTS_FAIL,
    LEAVE_GROUP,
    LEAVE_GROUP_START,
    LEAVE_GROUP_SUCCESS,
    LEAVE_GROUP_FAIL,
    DELETE_GROUP,
    DELETE_GROUP_START,
    DELETE_GROUP_SUCCESS,
    DELETE_GROUP_FAIL,
    SEARCH_GROUP,
    SEARCH_GROUP_START,
    SEARCH_GROUP_SUCCESS,
    SEARCH_GROUP_FAIL,
    JOIN_GROUP,
    JOIN_GROUP_START,
    JOIN_GROUP_SUCCESS,
    JOIN_GROUP_FAIL,
    CANCEL_REQUEST_JOIN_GROUP,
    CANCEL_REQUEST_JOIN_GROUP_SUCCESS,
    CANCEL_REQUEST_JOIN_GROUP_FAIL,
    FETCH_GROUP_MEMBER,
    FETCH_GROUP_MEMBER_ERROR,
    RECEIVED_GROUP_MEMBER_DATA,
    START_FETCHING_GROUP_MEMBER
} from 'actions/ActionTypes';

import { selectGroupSearchKeyword, selectGroupSearchStart, selectGroupInvitations } from 'reducers/group/groups';
import { GROUP_SEARCH_LIMIT, JOIN_GROUP_TYPE } from 'utils/groups/GroupsUtil';

// worker
export function* fetchGroups({ displayName }) {
    try {
        yield put({
            type: FETCH_GROUPS_START
        });
        const response = yield gcApi.get(`/group-service/groups/${displayName}?includeMembership=true`);
        yield put({
            type: FETCH_GROUPS_SUCCESS,
            data: response.data
        });

        const administeredGroups = response.data.filter((item) => item.membershipStatus !== 'MEMBER');
        yield put({
            type: FETCH_ADMINISTERED_GROUPS_SUCCESS,
            data: administeredGroups
        });
    } catch (e) {
        yield put({
            type: FETCH_GROUPS_FAIL
        });
    }
}

export function* fetchAdministeredGroups({ displayName }) {
    try {
        yield put({
            type: FETCH_ADMINISTERED_GROUPS_START
        });
        const response = yield gcApi.get(`group-service/groups/${displayName}?includeMembership=true`);
        const administeredGroups = response.data.filter((item) => item.membershipStatus !== 'MEMBER');
        yield put({
            type: FETCH_ADMINISTERED_GROUPS_SUCCESS,
            data: administeredGroups
        });
    } catch (e) {
        yield put({
            type: FETCH_ADMINISTERED_GROUPS_FAIL
        });
    }
}

export function* fetchGroupRequests({ displayName }) {
    try {
        yield put({
            type: FETCH_GROUP_REQUESTS_START
        });
        const response = yield gcApi.get(`/group-service/private/group/request/user/${displayName}`);
        yield put({
            type: FETCH_GROUP_REQUESTS_SUCCESS,
            data: response.data
        });
    } catch (e) {
        yield put({
            type: FETCH_GROUP_REQUESTS_FAIL
        });
    }
}

export function* fetchUnhandledGroupRequests({ displayName }) {
    try {
        const response = yield gcApi.get(`/group-service/requestNotHandled/${displayName}`);
        yield put({
            type: FETCH_UNHANDLED_GROUP_REQUESTS_SUCCESS,
            data: response.data
        });
    } catch (e) {
        yield put({
            type: FETCH_UNHANDLED_GROUP_REQUESTS_FAIL
        });
    }
}

export function* leaveGroup({ groupId, profileId }) {
    try {
        yield put({
            type: LEAVE_GROUP_START
        });
        yield call(gcApi.delete, `/group-service/group/${groupId}/member/${profileId}`);
        yield put({
            type: LEAVE_GROUP_SUCCESS,
            groupId
        });
    } catch (e) {
        yield put({
            type: LEAVE_GROUP_FAIL
        });
    }
}

export function* deleteGroup({ groupId }) {
    try {
        yield put({
            type: DELETE_GROUP_START
        });
        yield call(gcApi.delete, `/group-service/group/${groupId}`);
        yield put({
            type: DELETE_GROUP_SUCCESS,
            groupId
        });
    } catch (e) {
        yield put({
            type: DELETE_GROUP_FAIL
        });
    }
}

export function* searchGroup({ encodedKeyword, reset, forceSearch }) {
    const keyword = decodeURIComponent(encodedKeyword);
    try {
        const groupSearchKeyword = yield select(selectGroupSearchKeyword);

        /**
         * This check is to prevent the duplicated search with the same keyword, for example,
         * switch tab would cause GroupSearch component to re-render, and it'll try to search with the same keyword again
         */
        if (groupSearchKeyword === keyword && !forceSearch) return;

        yield put({
            type: SEARCH_GROUP_START,
            reset
        });

        const groupSearchStart = yield select(selectGroupSearchStart);
        const postData = new URLSearchParams();
        postData.append('keyword', keyword);
        postData.append('start', `${groupSearchStart}`);
        postData.append('limit', `${GROUP_SEARCH_LIMIT + 1}`);
        const response = yield gcApi({
            method: 'post',
            url: '/group-service/keyword',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
            data: postData
        });

        yield put({
            type: SEARCH_GROUP_SUCCESS,
            data: response.data,
            keyword,
            reset
        });
    } catch (e) {
        yield put({
            type: SEARCH_GROUP_FAIL,
            keyword
        });
    }
}

export function* joinGroup({ groupId, userProfileId, joinType }) {
    try {
        const groupInvitations = yield select(selectGroupInvitations);

        yield put({
            type: JOIN_GROUP_START
        });

        let response;
        switch (joinType) {
            case JOIN_GROUP_TYPE.requestJoin:
                response = yield call(gcApi.post, `/group-service/private/group/${groupId}`, {
                    userGroupId: groupId,
                    requestorId: userProfileId
                });
                break;
            case JOIN_GROUP_TYPE.acceptInvitation:
                const { groupInviteId } = groupInvitations.find((invite) => invite.groupId === groupId);
                response = yield call(gcApi.put, `/group-service/invite/group/${groupId}`, {
                    userGroupId: groupId,
                    groupInviteId
                });
                break;
            case JOIN_GROUP_TYPE.joinInstantly:
            default:
                response = yield call(gcApi.post, `/group-service/group/${groupId}/member/${userProfileId}`, {
                    groupId,
                    userProfileId
                });
                break;
        }

        yield put({
            type: JOIN_GROUP_SUCCESS,
            groupId,
            joinType,
            groupRequest: joinType !== JOIN_GROUP_TYPE.acceptInvitation ? response.data : null
        });
    } catch (e) {
        yield put({
            type: JOIN_GROUP_FAIL
        });
    }
}

export function* cancelRequestJoinGroup({ groupId, groupMemberRequestId }) {
    try {
        yield call(gcApi.delete, `/group-service/private/group/${groupId}`, {
            data: {
                userGroupId: groupId,
                groupMemberRequestId
            }
        });
        yield put({
            type: CANCEL_REQUEST_JOIN_GROUP_SUCCESS,
            groupId
        });
    } catch (e) {
        yield put({
            type: CANCEL_REQUEST_JOIN_GROUP_FAIL
        });
    }
}

export function* fetchGroupsMemberSaga({ groupId, userProfileId }) {
    try {
        yield put({
            type: START_FETCHING_GROUP_MEMBER
        });
        const response = yield gcApi.get(`group-service/group/${groupId}/member/${userProfileId}`);
        yield put({
            type: RECEIVED_GROUP_MEMBER_DATA,
            data: response.data
        });
    } catch (e) {
        yield put({
            type: FETCH_GROUP_MEMBER_ERROR
        });
    }
}

// watcher
export const groupSagas = [
    takeEvery(FETCH_GROUPS, fetchGroups),
    takeEvery(FETCH_ADMINISTERED_GROUPS, fetchAdministeredGroups),
    takeEvery(FETCH_GROUP_REQUESTS, fetchGroupRequests),
    takeEvery(FETCH_UNHANDLED_GROUP_REQUESTS, fetchUnhandledGroupRequests),
    takeEvery(LEAVE_GROUP, leaveGroup),
    takeEvery(DELETE_GROUP, deleteGroup),
    takeEvery(SEARCH_GROUP, searchGroup),
    takeEvery(JOIN_GROUP, joinGroup),
    takeEvery(CANCEL_REQUEST_JOIN_GROUP, cancelRequestJoinGroup),
    takeEvery(FETCH_GROUP_MEMBER, fetchGroupsMemberSaga)
];
